import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemDatePickerPopup } from "../../../../../components/UIToolkit/Popups/SystemDatePickerPopup";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useIsReadOnlyForDepartment } from "../../../../../hooks/useIsReadOnlyForDepartment";
import type { NurseWithExternalData } from "../../../../../utils/models";
import { useApplyPrimaryJob } from "../useApplyPrimaryJob";
import { JobNotificationChips } from "./JobNotificationChips";

type Props = { nurse: NurseWithExternalData };

/** The Job field in view mode: job chips that toggle notifications, plus "Apply primary job to shifts". */
export const JobFieldView: FC<Props> = ({ nurse }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const isReadOnly = useIsReadOnlyForDepartment();
	const { anchorRef, pickerOpen, setPickerOpen, loading, selectedDate, handleDateChange, handleSave } = useApplyPrimaryJob(nurse.id);
	// DEFAULT: `jobs` is optional on the profile. No jobs shows "--".
	const jobs = nurse.jobs ?? [];

	if (jobs.length === 0) return <Box component="span">--</Box>;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, maxWidth: "100%" }}>
			<JobNotificationChips departmentId={chosenDepartment.department_id} nurseId={nurse.id} firstName={nurse.firstName} jobs={jobs} canToggle={!isReadOnly} />
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
				<SystemButton
					ref={anchorRef}
					buttonVariant="hairline"
					size="compact"
					noFullWidthMobile
					disabled={loading}
					tooltip={t("apply.primary.job.to.shifts.description")}
					onClick={() => setPickerOpen(true)}
					data-testid="profile-apply-primary-job-button"
				>
					{t("apply.primary.job.to.shifts")}
					{selectedDate ? `: ${selectedDate.toFormat("MMM d, yyyy")}` : ""}
				</SystemButton>
				{selectedDate && (
					<SystemButton size="compact" noFullWidthMobile loading={loading} onClick={handleSave} data-testid="profile-apply-primary-job-save">
						{t("save")}
					</SystemButton>
				)}
			</Box>
			<SystemDatePickerPopup
				open={pickerOpen}
				anchorEl={anchorRef.current}
				value={selectedDate}
				// Safe cast: the date picker uses Luxon app-wide.
				onChange={(date) => handleDateChange(date as DateTime | null)}
				onClose={() => setPickerOpen(false)}
				datePickerToolbarTitle={t("apply.primary.job.to.shifts")}
			/>
		</Box>
	);
};
